import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  ['terminology', 'Our terminology', 'e.g. we say "applicant" not "candidate"; "Lead Specialist" not "SDR"'],
  ['departments', 'Department names', 'e.g. Recruitment, Training, HR Ops, Client Delivery'],
  ['tone', 'Writing tone', 'e.g. plain English, direct, no marketing language, UK spelling'],
  ['structure', 'Standard page structure', 'e.g. every SOP starts with Purpose then Scope then Steps'],
  ['do_not_use', 'Never write', 'e.g. never state salary bands or legal notice periods'],
];

export const AiSettingsDialog = ({ open, onOpenChange }) => {
  const [context, setContext] = useState({});
  const [busy, setBusy] = useState(false);

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/kb/ai/settings`, cfg())
      .then(res => setContext(res.data.company_context || {}))
      .catch(() => toast.error('Could not load AI settings'));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/kb/ai/settings/company-context`, {
        terminology: context.terminology || '', departments: context.departments || '',
        tone: context.tone || '', structure: context.structure || '', do_not_use: context.do_not_use || ''
      }, cfg());
      toast.success('AI settings saved');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Only admins can change AI settings' : 'Could not save AI settings');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="kb-ai-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" />Officium AI settings</DialogTitle>
          <DialogDescription>
            The company context the assistant must follow. Admins only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {FIELDS.map(([key, label, hint]) => (
            <div key={key}>
              <Label>{label}</Label>
              <Textarea rows={2} className="mt-1.5" placeholder={hint} value={context[key] || ''}
                data-testid={`kb-ai-context-${key}`}
                onChange={e => setContext(prev => ({ ...prev, [key]: e.target.value }))} />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="kb-ai-settings-save">{busy ? 'Saving…' : 'Save settings'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
